import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminLayout from '../../components/layout/AdminLayout';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../../components/ui/dialog';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../../components/ui/tabs';
import { Switch } from '../../components/ui/switch';
import { adminAPI } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import { toast } from 'sonner';
import AdvancedFinancials from './AdvancedFinancials';
import GeographicDistribution from './GeographicDistribution';
import { 
  Search, 
  Shield, 
  AlertTriangle, 
  User, 
  Building2, 
  Eye, 
  FileText, 
  Clock, 
  Globe, 
  Monitor,
  CheckCircle,
  XCircle,
  Loader2,
  Skull,
  History,
  Scale,
  LayoutDashboard,
  DollarSign,
  MapPin,
  Activity,
  Bell,
  UserPlus,
  CreditCard,
  Briefcase,
  LogIn,
  FileCheck,
  AlertCircle,
  RefreshCw,
  ArrowLeft,
  Home,
  Users,
  Flag,
  TrendingUp,
  Timer,
  Layers,
  ToggleLeft,
  Megaphone,
  Info,
  Calendar,
  Trash2,
  Send,
  StopCircle
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminCommandCenter = () => {
  const { startImpersonation, user, getToken } = useAuth();
  const navigate = useNavigate();
  
  // Tab state - default to 'home' (the new L1 tab)
  const [activeTab, setActiveTab] = useState('home');
  
  // L1: Command Center Home state
  const [commandCenterData, setCommandCenterData] = useState(null);
  const [loadingCommandCenter, setLoadingCommandCenter] = useState(false);
  const [togglingFlag, setTogglingFlag] = useState(null);
  
  // Search state
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  
  // Selected user state
  const [selectedUser, setSelectedUser] = useState(null);
  const [loadingUser, setLoadingUser] = useState(false);
  
  // FCRA Audit state
  const [fcraAudit, setFcraAudit] = useState(null);
  const [loadingFcra, setLoadingFcra] = useState(false);
  
  // Impersonation state
  const [showImpersonateConfirm, setShowImpersonateConfirm] = useState(false);
  const [impersonating, setImpersonating] = useState(false);
  
  // Impersonation logs
  const [impersonationLogs, setImpersonationLogs] = useState([]);
  const [loadingLogs, setLoadingLogs] = useState(false);
  
  // Activity Firehose state
  const [firehoseEvents, setFirehoseEvents] = useState([]);
  const [loadingFirehose, setLoadingFirehose] = useState(false);

  // N4: Broadcasts state
  const [broadcasts, setBroadcasts] = useState([]);
  const [loadingBroadcasts, setLoadingBroadcasts] = useState(false);
  const [showBroadcastForm, setShowBroadcastForm] = useState(false);
  const [broadcastForm, setBroadcastForm] = useState({
    title: '',
    body: '',
    audience: [],
    severity: 'info',
    ends_at: '',
  });
  const [creatingBroadcast, setCreatingBroadcast] = useState(false);
  const [endingBroadcast, setEndingBroadcast] = useState(null);

  // L1: Fetch Command Center data
  const loadCommandCenterData = useCallback(async () => {
    setLoadingCommandCenter(true);
    try {
      const token = getToken();
      const response = await fetch(`${API_URL}/api/admin/command-center`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setCommandCenterData(data);
      } else {
        toast.error('Failed to load command center data');
      }
    } catch (error) {
      console.error('Command center error:', error);
      toast.error('Failed to load command center data');
    } finally {
      setLoadingCommandCenter(false);
    }
  }, [getToken]);

  // L1: Toggle feature flag
  const handleToggleFlag = async (flagKey, currentEnabled) => {
    setTogglingFlag(flagKey);
    try {
      const token = getToken();
      const response = await fetch(`${API_URL}/api/admin/feature-flags/${flagKey}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ enabled: !currentEnabled })
      });
      
      if (response.ok) {
        toast.success(`${flagKey} ${!currentEnabled ? 'enabled' : 'disabled'}`);
        // Refresh command center data to get updated flags
        await loadCommandCenterData();
      } else {
        toast.error('Failed to toggle flag');
      }
    } catch (error) {
      console.error('Toggle flag error:', error);
      toast.error('Failed to toggle flag');
    } finally {
      setTogglingFlag(null);
    }
  };

  // Load command center data on mount and when tab changes to home
  useEffect(() => {
    if (activeTab === 'home') {
      loadCommandCenterData();
    }
  }, [activeTab, loadCommandCenterData]);

  // Search users
  const handleSearch = async () => {
    if (!searchQuery || searchQuery.length < 2) {
      toast.error('Enter at least 2 characters');
      return;
    }
    
    setSearching(true);
    try {
      const response = await adminAPI.godModeSearch(searchQuery);
      setSearchResults(response.data.results);
      if (response.data.results.length === 0) {
        toast.info('No users found');
      }
    } catch (error) {
      console.error('Search error:', error);
      toast.error('Search failed');
    } finally {
      setSearching(false);
    }
  };

  // Load full user details
  const loadUserDetails = async (userId) => {
    setLoadingUser(true);
    setSelectedUser(null);
    setFcraAudit(null);
    
    try {
      const response = await adminAPI.godModeGetUser(userId);
      setSelectedUser(response.data);
    } catch (error) {
      console.error('Error loading user:', error);
      toast.error('Failed to load user details');
    } finally {
      setLoadingUser(false);
    }
  };

  // Load FCRA audit
  const loadFcraAudit = async (userId) => {
    setLoadingFcra(true);
    try {
      const response = await adminAPI.getFcraAudit(userId);
      setFcraAudit(response.data);
    } catch (error) {
      console.error('Error loading FCRA audit:', error);
      toast.error('Failed to load FCRA audit');
    } finally {
      setLoadingFcra(false);
    }
  };

  // Load impersonation logs
  const loadImpersonationLogs = async () => {
    setLoadingLogs(true);
    try {
      const response = await adminAPI.getImpersonationLogs();
      setImpersonationLogs(response.data.logs);
    } catch (error) {
      console.error('Error loading logs:', error);
    } finally {
      setLoadingLogs(false);
    }
  };

  // Load activity firehose
  const loadFirehose = useCallback(async () => {
    setLoadingFirehose(true);
    try {
      const response = await adminAPI.getActivityFirehose(50);
      setFirehoseEvents(response.data.events || []);
    } catch (error) {
      console.error('Error loading firehose:', error);
    } finally {
      setLoadingFirehose(false);
    }
  }, []);

  // Handle impersonation
  const handleImpersonate = async () => {
    if (!selectedUser) return;
    
    setImpersonating(true);
    try {
      const response = await adminAPI.impersonateUser(selectedUser.user.id);
      const { impersonation_token, target_user } = response.data;
      
      // Start impersonation via AuthContext
      startImpersonation(impersonation_token, target_user);
      
      toast.success(`Now impersonating ${target_user.email}`);
      setShowImpersonateConfirm(false);
    } catch (error) {
      console.error('Impersonation error:', error);
      toast.error(error.response?.data?.detail || 'Impersonation failed');
    } finally {
      setImpersonating(false);
    }
  };

  useEffect(() => {
    loadImpersonationLogs();
    loadFirehose();
    
    // Set up polling for firehose (every 30 seconds)
    const interval = setInterval(loadFirehose, 30000);
    return () => clearInterval(interval);
  }, [loadFirehose]);

  // N4: Load broadcasts
  const loadBroadcasts = useCallback(async () => {
    setLoadingBroadcasts(true);
    try {
      const token = getToken();
      const response = await fetch(`${API_URL}/api/admin/broadcasts?include_ended=true`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setBroadcasts(data.broadcasts || []);
      }
    } catch (error) {
      console.error('Load broadcasts error:', error);
    } finally {
      setLoadingBroadcasts(false);
    }
  }, [getToken]);

  // N4: Create broadcast
  const handleCreateBroadcast = async () => {
    if (!broadcastForm.title.trim()) {
      toast.error('Title is required');
      return;
    }
    if (!broadcastForm.body.trim()) {
      toast.error('Body is required');
      return;
    }
    if (broadcastForm.audience.length === 0) {
      toast.error('Select at least one audience');
      return;
    }

    setCreatingBroadcast(true);
    try {
      const token = getToken();
      const payload = {
        ...broadcastForm,
        ends_at: broadcastForm.ends_at || null,
      };

      const response = await fetch(`${API_URL}/api/admin/broadcasts`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(payload)
      });

      if (response.ok) {
        toast.success('Broadcast created successfully');
        setBroadcastForm({ title: '', body: '', audience: [], severity: 'info', ends_at: '' });
        setShowBroadcastForm(false);
        await loadBroadcasts();
      } else {
        const err = await response.json();
        toast.error(err.detail || 'Failed to create broadcast');
      }
    } catch (error) {
      console.error('Create broadcast error:', error);
      toast.error('Failed to create broadcast');
    } finally {
      setCreatingBroadcast(false);
    }
  };

  // N4: End broadcast
  const handleEndBroadcast = async (broadcastId) => {
    setEndingBroadcast(broadcastId);
    try {
      const token = getToken();
      const response = await fetch(`${API_URL}/api/admin/broadcasts/${broadcastId}/end`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.ok) {
        toast.success('Broadcast ended');
        await loadBroadcasts();
      } else {
        toast.error('Failed to end broadcast');
      }
    } catch (error) {
      console.error('End broadcast error:', error);
      toast.error('Failed to end broadcast');
    } finally {
      setEndingBroadcast(null);
    }
  };

  // N4: Load broadcasts when broadcasts tab is active
  useEffect(() => {
    if (activeTab === 'broadcasts') {
      loadBroadcasts();
    }
  }, [activeTab, loadBroadcasts]);

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleString();
  };

  const formatRelativeTime = (dateStr) => {
    if (!dateStr) return 'N/A';
    const date = new Date(dateStr);
    const now = new Date();
    const diff = now - date;
    
    if (diff < 60000) return 'just now';
    if (diff < 3600000) return `${Math.floor(diff / 60000)}m ago`;
    if (diff < 86400000) return `${Math.floor(diff / 3600000)}h ago`;
    return `${Math.floor(diff / 86400000)}d ago`;
  };

  // Get icon for event type
  const getEventIcon = (eventType) => {
    const icons = {
      user_signup: <UserPlus className="w-4 h-4 text-green-400" />,
      user_login: <LogIn className="w-4 h-4 text-blue-400" />,
      record_created: <FileCheck className="w-4 h-4 text-purple-400" />,
      record_acknowledged: <CheckCircle className="w-4 h-4 text-green-400" />,
      record_disputed: <AlertCircle className="w-4 h-4 text-orange-400" />,
      subscription_change: <CreditCard className="w-4 h-4 text-blue-400" />,
      subscription_cancelled: <AlertTriangle className="w-4 h-4 text-red-400" />,
      trial_started: <Clock className="w-4 h-4 text-yellow-400" />,
      trial_expired: <Clock className="w-4 h-4 text-orange-400" />,
      god_mode_impersonation: <Skull className="w-4 h-4 text-red-400" />,
      user_impersonation: <Skull className="w-4 h-4 text-red-400" />,
      payment_succeeded: <DollarSign className="w-4 h-4 text-green-400" />,
      payment_failed: <DollarSign className="w-4 h-4 text-red-400" />,
      access_request: <Eye className="w-4 h-4 text-blue-400" />,
      access_granted: <CheckCircle className="w-4 h-4 text-green-400" />,
    };
    return icons[eventType] || <Activity className="w-4 h-4 text-[#5B6472]" />;
  };

  // L1: Render the Home tab (Pulse + Nav Counts + Feature Flags)
  const renderHomeContent = () => {
    if (loadingCommandCenter && !commandCenterData) {
      return (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="w-8 h-8 animate-spin text-[#1B5AFF]" />
        </div>
      );
    }

    if (!commandCenterData) {
      return (
        <div className="text-center py-20 text-[#5B6472]">
          <AlertCircle className="w-12 h-12 mx-auto mb-4 opacity-50" />
          <p>Failed to load command center data</p>
          <Button onClick={loadCommandCenterData} className="mt-4">Retry</Button>
        </div>
      );
    }

    const { records, signups, revenue, queue, time_to_lock, completion_by_flow, nav_counts, feature_flags } = commandCenterData;

    return (
      <div className="space-y-6">
        {/* Nav Counts - Universal Admin Navigation */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card className="bg-[#FFFFFF] border border-[#E8EDF5] rounded-xl p-5">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-lg bg-green-500/20 flex items-center justify-center">
                <Users className="w-5 h-5 text-green-400" />
              </div>
              <div>
                <p className="text-[#5B6472] text-sm">Total Professionals</p>
                <p className="text-2xl font-bold text-[#0A0F1E]">{nav_counts?.total_professionals?.toLocaleString() || 0}</p>
              </div>
            </div>
          </Card>

          <Card className="bg-[#FFFFFF] border border-[#E8EDF5] rounded-xl p-5">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-lg bg-blue-500/20 flex items-center justify-center">
                <Building2 className="w-5 h-5 text-blue-400" />
              </div>
              <div>
                <p className="text-[#5B6472] text-sm">Total Employers</p>
                <p className="text-2xl font-bold text-[#0A0F1E]">{nav_counts?.total_employers?.toLocaleString() || 0}</p>
              </div>
            </div>
          </Card>

          <Card className="bg-[#FFFFFF] border border-[#E8EDF5] rounded-xl p-5">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-lg bg-orange-500/20 flex items-center justify-center">
                <AlertCircle className="w-5 h-5 text-orange-400" />
              </div>
              <div>
                <p className="text-[#5B6472] text-sm">Pending Actions</p>
                <p className="text-2xl font-bold text-[#0A0F1E]">{nav_counts?.pending_admin_actions || 0}</p>
              </div>
            </div>
            {nav_counts?.pending_breakdown && (
              <div className="text-xs text-[#5B6472] space-y-1 mt-2 pt-2 border-t border-[#E8EDF5]">
                <div className="flex justify-between"><span>Queue Items</span><span>{nav_counts.pending_breakdown.queue_items || 0}</span></div>
                <div className="flex justify-between"><span>Access Requests</span><span>{nav_counts.pending_breakdown.access_requests || 0}</span></div>
                <div className="flex justify-between"><span>Record Requests</span><span>{nav_counts.pending_breakdown.record_requests || 0}</span></div>
                <div className="flex justify-between"><span>Flagged Records</span><span>{nav_counts.pending_breakdown.flagged_records || 0}</span></div>
                <div className="flex justify-between"><span>Flagged Users</span><span>{nav_counts.pending_breakdown.flagged_users || 0}</span></div>
              </div>
            )}
          </Card>
        </div>

        {/* D5 Pulse Metrics - Records & Signups */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <Card className="bg-[#FFFFFF] border border-[#E8EDF5] rounded-xl p-5">
            <div className="flex items-center gap-2 mb-2">
              <FileText className="w-4 h-4 text-[#1B5AFF]" />
              <span className="text-[#5B6472] text-sm">Records This Week</span>
            </div>
            <p className="text-xl font-bold text-[#0A0F1E]">{records?.requested_this_week || 0} requested</p>
            <p className="text-sm text-green-400">{records?.locked_this_week || 0} locked ({records?.lock_rate || 0}%)</p>
          </Card>

          <Card className="bg-[#FFFFFF] border border-[#E8EDF5] rounded-xl p-5">
            <div className="flex items-center gap-2 mb-2">
              <UserPlus className="w-4 h-4 text-[#1B5AFF]" />
              <span className="text-[#5B6472] text-sm">Signups This Week</span>
            </div>
            <p className="text-xl font-bold text-[#0A0F1E]">{signups?.employees_this_week || 0} professionals</p>
            <p className="text-sm text-blue-400">{signups?.employers_this_week || 0} employers</p>
          </Card>

          <Card className="bg-[#FFFFFF] border border-[#E8EDF5] rounded-xl p-5">
            <div className="flex items-center gap-2 mb-2">
              <DollarSign className="w-4 h-4 text-[#1B5AFF]" />
              <span className="text-[#5B6472] text-sm">MRR</span>
            </div>
            <p className="text-xl font-bold text-[#0A0F1E]">${revenue?.mrr_calculated?.toLocaleString() || 0}</p>
            <p className="text-sm text-[#5B6472]">{revenue?.active_subscriptions || 0} active subs</p>
          </Card>

          <Card className="bg-[#FFFFFF] border border-[#E8EDF5] rounded-xl p-5">
            <div className="flex items-center gap-2 mb-2">
              <Timer className="w-4 h-4 text-[#1B5AFF]" />
              <span className="text-[#5B6472] text-sm">Time to Lock</span>
            </div>
            <p className="text-xl font-bold text-[#0A0F1E]">{time_to_lock?.avg_days?.toFixed(1) || 0} days avg</p>
            <p className="text-sm text-[#5B6472]">{time_to_lock?.sample_size || 0} records</p>
          </Card>
        </div>

        {/* Queue Health & Completion by Flow */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Card className="bg-[#FFFFFF] border border-[#E8EDF5] rounded-xl p-5">
            <div className="flex items-center gap-2 mb-4">
              <Layers className="w-5 h-5 text-[#1B5AFF]" />
              <h3 className="text-[#0A0F1E] font-semibold">Queue Health</h3>
            </div>
            <div className="space-y-3">
              <div className="flex justify-between items-center">
                <span className="text-[#5B6472]">Pending</span>
                <span className="text-[#0A0F1E] font-semibold">{queue?.pending_count || 0}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-[#5B6472]">Overdue</span>
                <span className={`font-semibold ${(queue?.overdue_count || 0) > 0 ? 'text-orange-400' : 'text-green-400'}`}>
                  {queue?.overdue_count || 0}
                </span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-[#5B6472]">Oldest Age</span>
                <span className="text-[#0A0F1E]">{queue?.oldest_age_hours?.toFixed(1) || 0}h</span>
              </div>
            </div>
          </Card>

          <Card className="bg-[#FFFFFF] border border-[#E8EDF5] rounded-xl p-5">
            <div className="flex items-center gap-2 mb-4">
              <TrendingUp className="w-5 h-5 text-[#1B5AFF]" />
              <h3 className="text-[#0A0F1E] font-semibold">Completion by Flow</h3>
            </div>
            <div className="space-y-3">
              <div className="flex justify-between items-center">
                <span className="text-[#5B6472]">Full Flow</span>
                <span className="text-[#0A0F1E] font-semibold">{completion_by_flow?.full?.rate || 0}%</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-[#5B6472]">Manager Only</span>
                <span className="text-[#0A0F1E]">{completion_by_flow?.manager_only?.rate || 0}%</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-[#5B6472]">HR Only</span>
                <span className="text-[#0A0F1E]">{completion_by_flow?.hr_only?.rate || 0}%</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-[#5B6472]">Single Signatory</span>
                <span className="text-[#0A0F1E]">{completion_by_flow?.single_signatory?.rate || 0}%</span>
              </div>
            </div>
          </Card>
        </div>

        {/* Feature Flags Panel */}
        <Card className="bg-[#FFFFFF] border border-[#E8EDF5] rounded-xl p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <Flag className="w-5 h-5 text-[#1B5AFF]" />
              <h3 className="text-[#0A0F1E] font-semibold">Feature Flags</h3>
            </div>
            <Button
              onClick={loadCommandCenterData}
              variant="ghost"
              size="sm"
              className="text-[#5B6472] hover:text-[#0A0F1E]"
              disabled={loadingCommandCenter}
            >
              <RefreshCw className={`w-4 h-4 ${loadingCommandCenter ? 'animate-spin' : ''}`} />
            </Button>
          </div>
          
          <div className="space-y-3">
            {feature_flags && feature_flags.length > 0 ? (
              feature_flags.map((flag) => (
                <div 
                  key={flag.key} 
                  className="flex items-center justify-between p-3 bg-[#F5F8FF] rounded-lg border border-[#E8EDF5]"
                >
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <span className="text-[#0A0F1E] font-medium">{flag.key}</span>
                      <Badge 
                        variant="outline" 
                        className={flag.enabled ? 'border-green-500 text-green-400' : 'border-[#5B6472] text-[#5B6472]'}
                      >
                        {flag.enabled ? 'ON' : 'OFF'}
                      </Badge>
                    </div>
                    {flag.description && (
                      <p className="text-[#5B6472] text-xs mt-1">{flag.description}</p>
                    )}
                    {flag.visible_to && flag.visible_to.length > 0 && (
                      <div className="flex gap-1 mt-1">
                        {flag.visible_to.map((audience) => (
                          <Badge key={audience} variant="secondary" className="text-xs bg-[#F5F8FF]">
                            {audience}
                          </Badge>
                        ))}
                      </div>
                    )}
                  </div>
                  <div className="ml-4">
                    <Switch
                      checked={flag.enabled}
                      onCheckedChange={() => handleToggleFlag(flag.key, flag.enabled)}
                      disabled={togglingFlag === flag.key}
                      data-testid={`flag-toggle-${flag.key}`}
                    />
                  </div>
                </div>
              ))
            ) : (
              <p className="text-[#5B6472] text-center py-4">No feature flags configured</p>
            )}
          </div>
        </Card>
      </div>
    );
  };

  // Render the Overview (God Mode) content
  const renderOverviewContent = () => (
    <div className="space-y-6">
      {/* Search Section */}
      <Card className="bg-[#FFFFFF] border border-[#E8EDF5] rounded-xl p-6">
        <div className="flex items-center gap-3 mb-4">
          <Search className="w-5 h-5 text-[#1B5AFF]" />
          <h2 className="text-lg font-semibold text-[#0A0F1E]">User Search & Control</h2>
        </div>
        
        <div className="flex gap-3 mb-6">
          <Input
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
            placeholder="Search by Email or VETT ID..."
            className="flex-1 bg-[#F5F8FF] border-[#E8EDF5] text-[#0A0F1E]"
            data-testid="god-mode-search-input"
          />
          <Button
            onClick={handleSearch}
            disabled={searching}
            className="bg-[#1B5AFF] hover:bg-[#1B5AFF] text-[#FFFFFF]"
            data-testid="god-mode-search-btn"
          >
            {searching ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
            <span className="ml-2">Search</span>
          </Button>
        </div>

        {/* Search Results */}
        {searchResults.length > 0 && (
          <div className="space-y-2 max-h-64 overflow-y-auto">
            {searchResults.map((result) => (
              <div
                key={result.user.id}
                onClick={() => loadUserDetails(result.user.id)}
                className={`p-3 rounded-lg border cursor-pointer transition-all ${
                  selectedUser?.user.id === result.user.id
                    ? 'bg-[#1B5AFF]/20 border-[#1B5AFF]'
                    : 'bg-[#F5F8FF] border-[#E8EDF5] hover:border-[#1B5AFF]/50'
                }`}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    {result.user.account_type === 'employer' ? (
                      <Building2 className="w-5 h-5 text-blue-400" />
                    ) : result.user.account_type === 'agency' ? (
                      <Briefcase className="w-5 h-5 text-purple-400" />
                    ) : (
                      <User className="w-5 h-5 text-green-400" />
                    )}
                    <div>
                      <p className="text-[#0A0F1E] font-medium">{result.user.email}</p>
                      <p className="text-[#5B6472] text-xs">
                        {result.user.vett_id} | {result.user.account_type}
                      </p>
                    </div>
                  </div>
                  <Badge variant={result.user.email_verified ? 'default' : 'destructive'}>
                    {result.user.email_verified ? 'Verified' : 'Unverified'}
                  </Badge>
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>

      {/* Selected User Details */}
      {loadingUser && (
        <Card className="bg-[#FFFFFF] border border-[#E8EDF5] rounded-xl p-6">
          <div className="flex items-center justify-center py-8">
            <Loader2 className="w-8 h-8 animate-spin text-[#1B5AFF]" />
          </div>
        </Card>
      )}

      {selectedUser && !loadingUser && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* User Info Card */}
          <Card className="bg-[#FFFFFF] border border-[#E8EDF5] rounded-xl p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <Shield className="w-5 h-5 text-[#1B5AFF]" />
                <h2 className="text-lg font-semibold text-[#0A0F1E]">User Details</h2>
              </div>
              {selectedUser.can_impersonate && (
                <Button
                  onClick={() => setShowImpersonateConfirm(true)}
                  className="bg-red-600 hover:bg-red-700 text-[#FFFFFF]"
                  data-testid="impersonate-btn"
                >
                  <Eye className="w-4 h-4 mr-2" />
                  Impersonate User
                </Button>
              )}
            </div>

            <div className="space-y-3">
              <div className="flex justify-between">
                <span className="text-[#5B6472]">Email</span>
                <span className="text-[#0A0F1E] font-mono">{selectedUser.user.email}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-[#5B6472]">VETT ID</span>
                <span className="text-[#0A0F1E] font-mono">{selectedUser.user.vett_id}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-[#5B6472]">Account Type</span>
                <Badge>{selectedUser.user.account_type}</Badge>
              </div>
              <div className="flex justify-between">
                <span className="text-[#5B6472]">Email Verified</span>
                {selectedUser.user.email_verified ? (
                  <CheckCircle className="w-5 h-5 text-green-500" />
                ) : (
                  <XCircle className="w-5 h-5 text-red-500" />
                )}
              </div>
              <div className="flex justify-between">
                <span className="text-[#5B6472]">Created</span>
                <span className="text-[#0A0F1E]">{formatDate(selectedUser.user.created_at)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-[#5B6472]">Records</span>
                <span className="text-[#0A0F1E]">{selectedUser.records_count}</span>
              </div>
              
              {selectedUser.profile && (
                <>
                  <hr className="border-[#E8EDF5] my-3" />
                  {selectedUser.user.account_type === 'employer' || selectedUser.user.account_type === 'agency' ? (
                    <>
                      <div className="flex justify-between">
                        <span className="text-[#5B6472]">Company</span>
                        <span className="text-[#0A0F1E]">{selectedUser.profile.company_name || 'Not set'}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-[#5B6472]">Subscription</span>
                        <Badge variant="outline">{selectedUser.profile.subscription_tier}</Badge>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-[#5B6472]">FCRA Accepted</span>
                        {selectedUser.profile.fcra_accepted ? (
                          <CheckCircle className="w-5 h-5 text-green-500" />
                        ) : (
                          <XCircle className="w-5 h-5 text-red-500" />
                        )}
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="flex justify-between">
                        <span className="text-[#5B6472]">Name</span>
                        <span className="text-[#0A0F1E]">
                          {selectedUser.profile.first_name} {selectedUser.profile.last_name}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-[#5B6472]">Pro Status</span>
                        <Badge variant={selectedUser.profile.is_pro ? 'default' : 'outline'}>
                          {selectedUser.profile.is_pro ? 'Pro' : 'Free'}
                        </Badge>
                      </div>
                    </>
                  )}
                </>
              )}
            </div>
          </Card>

          {/* Legal Consents Card */}
          <Card className="bg-[#FFFFFF] border border-[#E8EDF5] rounded-xl p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <FileText className="w-5 h-5 text-[#1B5AFF]" />
                <h2 className="text-lg font-semibold text-[#0A0F1E]">Legal Consents</h2>
              </div>
              {(selectedUser.user.account_type === 'employer' || selectedUser.user.account_type === 'agency') && (
                <Button
                  onClick={() => loadFcraAudit(selectedUser.user.id)}
                  disabled={loadingFcra}
                  variant="outline"
                  className="border-[#E8EDF5] text-[#0A0F1E] hover:bg-[#F5F8FF]"
                >
                  {loadingFcra ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Scale className="w-4 h-4 mr-2" />
                  )}
                  FCRA Audit
                </Button>
              )}
            </div>

            {selectedUser.legal_consents && selectedUser.legal_consents.length > 0 ? (
              <div className="space-y-3 max-h-80 overflow-y-auto">
                {selectedUser.legal_consents.map((consent, index) => (
                  <div key={index} className="bg-[#F5F8FF] rounded-lg p-3 border border-[#E8EDF5]">
                    <div className="flex items-center justify-between mb-2">
                      <Badge variant="outline" className="text-xs">
                        {consent.document_type.replace(/_/g, ' ').toUpperCase()}
                      </Badge>
                      <span className="text-[#5B6472] text-xs">{consent.version}</span>
                    </div>
                    <div className="space-y-1 text-xs">
                      <div className="flex items-center gap-2 text-[#5B6472]">
                        <Clock className="w-3 h-3" />
                        {formatDate(consent.consented_at)}
                      </div>
                      <div className="flex items-center gap-2 text-[#5B6472]">
                        <Globe className="w-3 h-3" />
                        {consent.ip_address || 'Unknown IP'}
                      </div>
                      <div className="flex items-center gap-2 text-[#5B6472]">
                        <Monitor className="w-3 h-3" />
                        <span className="truncate max-w-[200px]">{consent.user_agent || 'Unknown'}</span>
                      </div>
                      {consent.document_hash && (
                        <div className="text-[#666] font-mono text-[10px] truncate">
                          Hash: {consent.document_hash.substring(0, 32)}...
                        </div>
                      )}
                      {consent.migrated_from_legacy && (
                        <Badge variant="outline" className="text-[10px] mt-1">Migrated from legacy</Badge>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-[#5B6472]">
                <FileText className="w-8 h-8 mx-auto mb-2 opacity-50" />
                <p>No legal consents recorded</p>
              </div>
            )}
          </Card>
        </div>
      )}

      {/* FCRA Audit Modal/Section */}
      {fcraAudit && (
        <Card className="bg-[#FFFFFF] border border-red-500/30 rounded-xl p-6">
          <div className="flex items-center gap-3 mb-4">
            <Scale className="w-5 h-5 text-red-500" />
            <h2 className="text-lg font-semibold text-[#0A0F1E]">FCRA Compliance Audit</h2>
            <Badge variant="destructive">LEGAL DOCUMENT</Badge>
          </div>

          <div className="bg-[#FFFFFF] rounded-lg p-4 mb-4">
            <p className="text-[#5B6472] text-sm mb-2">Audit Generated</p>
            <p className="text-[#0A0F1E] font-mono">{formatDate(fcraAudit.audit_generated_at)}</p>
            <p className="text-[#5B6472] text-xs mt-1">Requested by: {fcraAudit.audit_requested_by}</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            <div className="bg-[#FFFFFF] rounded-lg p-4">
              <p className="text-[#5B6472] text-sm mb-1">Company</p>
              <p className="text-[#0A0F1E] font-semibold">{fcraAudit.company_name}</p>
            </div>
            <div className="bg-[#FFFFFF] rounded-lg p-4">
              <p className="text-[#5B6472] text-sm mb-1">Email</p>
              <p className="text-[#0A0F1E] font-mono">{fcraAudit.email}</p>
            </div>
          </div>

          <h3 className="text-[#0A0F1E] font-semibold mb-3">FCRA Consent Records</h3>
          {fcraAudit.fcra_consents && fcraAudit.fcra_consents.length > 0 ? (
            <div className="space-y-3">
              {fcraAudit.fcra_consents.map((consent, index) => (
                <div key={index} className="bg-[#FFFFFF] rounded-lg p-4 border border-[#E8EDF5]">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-[#5B6472] text-xs">Consented At</p>
                      <p className="text-[#0A0F1E] font-mono text-sm">{formatDate(consent.consented_at)}</p>
                    </div>
                    <div>
                      <p className="text-[#5B6472] text-xs">Version</p>
                      <p className="text-[#0A0F1E] font-mono text-sm">{consent.version}</p>
                    </div>
                    <div>
                      <p className="text-[#5B6472] text-xs">IP Address</p>
                      <p className="text-[#0A0F1E] font-mono text-sm">{consent.ip_address}</p>
                    </div>
                    <div>
                      <p className="text-[#5B6472] text-xs">User Agent</p>
                      <p className="text-[#0A0F1E] text-sm truncate">{consent.user_agent}</p>
                    </div>
                  </div>
                  {consent.document_hash && (
                    <div className="mt-3 pt-3 border-t border-[#E8EDF5]">
                      <p className="text-[#5B6472] text-xs">Document Hash (SHA256)</p>
                      <p className="text-green-400 font-mono text-xs break-all">{consent.document_hash}</p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4 text-center">
              <AlertTriangle className="w-8 h-8 text-red-500 mx-auto mb-2" />
              <p className="text-red-400 font-semibold">NO FCRA CONSENT ON RECORD</p>
              <p className="text-[#5B6472] text-sm">This employer has not accepted the FCRA agreement</p>
            </div>
          )}
        </Card>
      )}

      {/* Impersonation Logs */}
      <Card className="bg-[#FFFFFF] border border-[#E8EDF5] rounded-xl p-6">
        <div className="flex items-center gap-3 mb-4">
          <History className="w-5 h-5 text-[#1B5AFF]" />
          <h2 className="text-lg font-semibold text-[#0A0F1E]">Impersonation Audit Log</h2>
        </div>

        {loadingLogs ? (
          <div className="flex justify-center py-8">
            <Loader2 className="w-6 h-6 animate-spin text-[#1B5AFF]" />
          </div>
        ) : impersonationLogs.length > 0 ? (
          <div className="space-y-2 max-h-64 overflow-y-auto">
            {impersonationLogs.map((log) => (
              <div key={log.id} className="bg-[#F5F8FF] rounded-lg p-3 border border-[#E8EDF5]">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-[#0A0F1E] text-sm">
                      <span className="text-[#1B5AFF]">{log.admin_email}</span> impersonated{' '}
                      <span className="text-yellow-400">{log.details?.target_email}</span>
                    </p>
                    <p className="text-[#5B6472] text-xs">{formatDate(log.timestamp)}</p>
                  </div>
                  <Badge variant="outline" className="text-xs">
                    {log.details?.target_account_type}
                  </Badge>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-8 text-[#5B6472]">
            <Eye className="w-8 h-8 mx-auto mb-2 opacity-50" />
            <p>No impersonation logs</p>
          </div>
        )}
      </Card>
    </div>
  );

  const backToPlatformButton = user?.is_superadmin ? (
    <Button
      onClick={() => navigate('/founder/dashboard')}
      variant="outline"
      className="border-[#E8EDF5] text-[#0A0F1E] hover:bg-[#F5F8FF]"
      data-testid="back-to-platform-btn"
    >
      <ArrowLeft className="w-4 h-4 mr-2" />
      Back to Platform
    </Button>
  ) : null;

  return (
    <AdminLayout breadcrumbs={[{ label: 'Overview' }]} actions={backToPlatformButton}>
      <div className="flex gap-6" data-testid="admin-command-center">
        {/* Main Content Area */}
        <div className="flex-1 min-w-0">
          {/* Internal Header */}
          <div className="flex items-center gap-3 mb-6">
            <div className="w-12 h-12 rounded-xl" style={{ background: 'linear-gradient(120deg, #1B5AFF, #00C9A7)' }}>
              <div className="w-full h-full flex items-center justify-center">
                <Shield className="w-6 h-6 text-white" />
              </div>
            </div>
            <div>
              <h1 className="text-2xl font-bold" style={{ color: '#0A0F1E' }}>Operations Suite</h1>
              <p className="text-sm" style={{ fontFamily: "'DM Mono', monospace", color: '#5B6472' }}>God Mode | Financial Analytics | Geographic Intelligence</p>
            </div>
          </div>

        {/* Tab Navigation */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList className="bg-[#F5F8FF] border border-[#E8EDF5] p-1 rounded-xl">
            <TabsTrigger
              value="home"
              className="data-[state=active]:bg-[#1B5AFF] data-[state=active]:text-white text-[#5B6472] hover:text-[#0A0F1E] px-4 py-2 rounded-lg"
              data-testid="tab-home"
            >
              <Home className="w-4 h-4 mr-2" />
              Home
            </TabsTrigger>
            <TabsTrigger
              value="overview"
              className="data-[state=active]:bg-[#1B5AFF] data-[state=active]:text-white text-[#5B6472] hover:text-[#0A0F1E] px-4 py-2 rounded-lg"
              data-testid="tab-overview"
            >
              <Skull className="w-4 h-4 mr-2" />
              God Mode
            </TabsTrigger>
            <TabsTrigger
              value="financials"
              className="data-[state=active]:bg-[#1B5AFF] data-[state=active]:text-white text-[#5B6472] hover:text-[#0A0F1E] px-4 py-2 rounded-lg"
              data-testid="tab-financials"
            >
              <DollarSign className="w-4 h-4 mr-2" />
              Financials
            </TabsTrigger>
            <TabsTrigger
              value="geography"
              className="data-[state=active]:bg-[#1B5AFF] data-[state=active]:text-white text-[#5B6472] hover:text-[#0A0F1E] px-4 py-2 rounded-lg"
              data-testid="tab-geography"
            >
              <MapPin className="w-4 h-4 mr-2" />
              Geography
            </TabsTrigger>
            <TabsTrigger
              value="broadcasts"
              className="data-[state=active]:bg-[#1B5AFF] data-[state=active]:text-white text-[#5B6472] hover:text-[#0A0F1E] px-4 py-2 rounded-lg"
              data-testid="tab-broadcasts"
            >
              <Megaphone className="w-4 h-4 mr-2" />
              Broadcasts
            </TabsTrigger>
          </TabsList>

          <TabsContent value="home" className="mt-6">
            {renderHomeContent()}
          </TabsContent>

          <TabsContent value="overview" className="mt-6">
            {renderOverviewContent()}
          </TabsContent>

          <TabsContent value="financials" className="mt-6">
            <AdvancedFinancials />
          </TabsContent>

          <TabsContent value="geography" className="mt-6">
            <GeographicDistribution />
          </TabsContent>

          {/* N4: Broadcasts Tab */}
          <TabsContent value="broadcasts" className="mt-6">
            <div className="space-y-6">
              {/* Header */}
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-xl font-bold text-[#0A0F1E]">Platform Broadcasts</h2>
                  <p className="text-[#5B6472] text-sm">Send targeted announcements to professionals and employers</p>
                </div>
                <Button
                  onClick={() => setShowBroadcastForm(!showBroadcastForm)}
                  className="bg-[#1B5AFF] hover:bg-[#1B5AFF] text-[#FFFFFF]"
                  data-testid="create-broadcast-btn"
                >
                  <Megaphone className="w-4 h-4 mr-2" />
                  New Broadcast
                </Button>
              </div>

              {/* Broadcast Form */}
              {showBroadcastForm && (
                <Card className="bg-[#FFFFFF] border border-[#E8EDF5] rounded-xl p-6">
                  <h3 className="text-[#0A0F1E] font-semibold mb-4">Create Broadcast</h3>
                  
                  <div className="space-y-4">
                    <div>
                      <label className="text-[#5B6472] text-sm block mb-1">Title *</label>
                      <Input
                        value={broadcastForm.title}
                        onChange={(e) => setBroadcastForm(prev => ({ ...prev, title: e.target.value }))}
                        placeholder="Announcement title"
                        className="bg-[#FFFFFF] border-[#E8EDF5] text-[#0A0F1E]"
                        data-testid="broadcast-title-input"
                      />
                    </div>
                    
                    <div>
                      <label className="text-[#5B6472] text-sm block mb-1">Body *</label>
                      <Input
                        value={broadcastForm.body}
                        onChange={(e) => setBroadcastForm(prev => ({ ...prev, body: e.target.value }))}
                        placeholder="Announcement message"
                        className="bg-[#FFFFFF] border-[#E8EDF5] text-[#0A0F1E]"
                        data-testid="broadcast-body-input"
                      />
                    </div>
                    
                    <div>
                      <label className="text-[#5B6472] text-sm block mb-1">Audience *</label>
                      <div className="flex gap-4">
                        <label className="flex items-center gap-2 text-[#0A0F1E]">
                          <input
                            type="checkbox"
                            checked={broadcastForm.audience.includes('professional')}
                            onChange={(e) => {
                              setBroadcastForm(prev => ({
                                ...prev,
                                audience: e.target.checked
                                  ? [...prev.audience, 'professional']
                                  : prev.audience.filter(a => a !== 'professional')
                              }));
                            }}
                            className="w-4 h-4"
                            data-testid="audience-professional-checkbox"
                          />
                          <User className="w-4 h-4" />
                          Professionals
                        </label>
                        <label className="flex items-center gap-2 text-[#0A0F1E]">
                          <input
                            type="checkbox"
                            checked={broadcastForm.audience.includes('employer')}
                            onChange={(e) => {
                              setBroadcastForm(prev => ({
                                ...prev,
                                audience: e.target.checked
                                  ? [...prev.audience, 'employer']
                                  : prev.audience.filter(a => a !== 'employer')
                              }));
                            }}
                            className="w-4 h-4"
                            data-testid="audience-employer-checkbox"
                          />
                          <Building2 className="w-4 h-4" />
                          Employers
                        </label>
                      </div>
                    </div>
                    
                    <div>
                      <label className="text-[#5B6472] text-sm block mb-1">Severity</label>
                      <select
                        value={broadcastForm.severity}
                        onChange={(e) => setBroadcastForm(prev => ({ ...prev, severity: e.target.value }))}
                        className="w-full bg-[#FFFFFF] border border-[#E8EDF5] text-[#0A0F1E] rounded-md px-3 py-2"
                        data-testid="broadcast-severity-select"
                      >
                        <option value="info">Info (Blue)</option>
                        <option value="warning">Warning (Yellow)</option>
                        <option value="critical">Critical (Red)</option>
                      </select>
                    </div>
                    
                    <div>
                      <label className="text-[#5B6472] text-sm block mb-1">End Date (optional)</label>
                      <Input
                        type="datetime-local"
                        value={broadcastForm.ends_at}
                        onChange={(e) => setBroadcastForm(prev => ({ ...prev, ends_at: e.target.value ? new Date(e.target.value).toISOString() : '' }))}
                        className="bg-[#FFFFFF] border-[#E8EDF5] text-[#0A0F1E]"
                        data-testid="broadcast-ends-at-input"
                      />
                      <p className="text-[#666] text-xs mt-1">Leave empty for broadcast to run until manually ended</p>
                    </div>

                    {/* Live Preview */}
                    <div>
                      <label className="text-[#5B6472] text-sm block mb-2">Live Preview</label>
                      <div className={`rounded-lg border px-4 py-3 flex items-center gap-3 ${
                        broadcastForm.severity === 'critical' 
                          ? 'bg-[#450A0A] border-[#EF4444]/30' 
                          : broadcastForm.severity === 'warning'
                            ? 'bg-[#422006] border-[#F59E0B]/30'
                            : 'bg-[#1E3A5F] border-[#3B82F6]/30'
                      }`}>
                        {broadcastForm.severity === 'critical' ? (
                          <AlertCircle className="w-4 h-4 text-[#FCA5A5]" />
                        ) : broadcastForm.severity === 'warning' ? (
                          <AlertTriangle className="w-4 h-4 text-[#FCD34D]" />
                        ) : (
                          <Info className="w-4 h-4 text-[#93C5FD]" />
                        )}
                        <div className={`flex-1 text-sm ${
                          broadcastForm.severity === 'critical'
                            ? 'text-[#FCA5A5]'
                            : broadcastForm.severity === 'warning'
                              ? 'text-[#FCD34D]'
                              : 'text-[#93C5FD]'
                        }`}>
                          <span className="font-medium">{broadcastForm.title || 'Title'}</span>
                          {broadcastForm.body && <span className="opacity-80"> - {broadcastForm.body}</span>}
                        </div>
                      </div>
                    </div>
                    
                    <div className="flex gap-3 pt-2">
                      <Button
                        onClick={handleCreateBroadcast}
                        disabled={creatingBroadcast}
                        className="bg-[#1B5AFF] hover:bg-[#1B5AFF] text-[#FFFFFF]"
                        data-testid="submit-broadcast-btn"
                      >
                        {creatingBroadcast ? (
                          <Loader2 className="w-4 h-4 animate-spin mr-2" />
                        ) : (
                          <Send className="w-4 h-4 mr-2" />
                        )}
                        Publish Broadcast
                      </Button>
                      <Button
                        onClick={() => setShowBroadcastForm(false)}
                        variant="outline"
                        className="border-[#E8EDF5] text-[#0A0F1E]"
                      >
                        Cancel
                      </Button>
                    </div>
                  </div>
                </Card>
              )}

              {/* Broadcasts List */}
              <Card className="bg-[#FFFFFF] border border-[#E8EDF5] rounded-xl p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-[#0A0F1E] font-semibold">All Broadcasts</h3>
                  <Button
                    onClick={loadBroadcasts}
                    variant="ghost"
                    size="sm"
                    disabled={loadingBroadcasts}
                    className="text-[#5B6472] hover:text-[#0A0F1E]"
                  >
                    <RefreshCw className={`w-4 h-4 ${loadingBroadcasts ? 'animate-spin' : ''}`} />
                  </Button>
                </div>

                {loadingBroadcasts && broadcasts.length === 0 ? (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 className="w-6 h-6 animate-spin text-[#1B5AFF]" />
                  </div>
                ) : broadcasts.length === 0 ? (
                  <div className="text-center py-8 text-[#5B6472]">
                    <Megaphone className="w-8 h-8 mx-auto mb-2 opacity-50" />
                    <p>No broadcasts yet</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {broadcasts.map((broadcast) => (
                      <div
                        key={broadcast.id}
                        className={`bg-[#FFFFFF] rounded-lg p-4 border ${
                          broadcast.ended_at
                            ? 'border-[#E8EDF5] opacity-60'
                            : broadcast.severity === 'critical'
                              ? 'border-red-500/30'
                              : broadcast.severity === 'warning'
                                ? 'border-yellow-500/30'
                                : 'border-blue-500/30'
                        }`}
                        data-testid={`broadcast-item-${broadcast.id}`}
                      >
                        <div className="flex items-start justify-between gap-4">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              <span className="text-[#0A0F1E] font-medium">{broadcast.title}</span>
                              <Badge variant="outline" className={`text-xs ${
                                broadcast.severity === 'critical'
                                  ? 'border-red-500/50 text-red-400'
                                  : broadcast.severity === 'warning'
                                    ? 'border-yellow-500/50 text-yellow-400'
                                    : 'border-blue-500/50 text-blue-400'
                              }`}>
                                {broadcast.severity}
                              </Badge>
                              {broadcast.ended_at && (
                                <Badge variant="outline" className="text-xs border-[#666] text-[#666]">
                                  Ended
                                </Badge>
                              )}
                            </div>
                            <p className="text-[#5B6472] text-sm">{broadcast.body}</p>
                            <div className="flex items-center gap-4 mt-2 text-xs text-[#666]">
                              <span className="flex items-center gap-1">
                                <Users className="w-3 h-3" />
                                {broadcast.audience?.join(', ')}
                              </span>
                              <span className="flex items-center gap-1">
                                <Clock className="w-3 h-3" />
                                {new Date(broadcast.created_at).toLocaleDateString()}
                              </span>
                              {broadcast.ends_at && !broadcast.ended_at && (
                                <span className="flex items-center gap-1">
                                  <Calendar className="w-3 h-3" />
                                  Ends: {new Date(broadcast.ends_at).toLocaleDateString()}
                                </span>
                              )}
                            </div>
                          </div>
                          {!broadcast.ended_at && (
                            <Button
                              onClick={() => handleEndBroadcast(broadcast.id)}
                              disabled={endingBroadcast === broadcast.id}
                              variant="outline"
                              size="sm"
                              className="border-red-500/50 text-red-400 hover:bg-red-500/10"
                              data-testid={`end-broadcast-${broadcast.id}`}
                            >
                              {endingBroadcast === broadcast.id ? (
                                <Loader2 className="w-4 h-4 animate-spin" />
                              ) : (
                                <StopCircle className="w-4 h-4" />
                              )}
                            </Button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </Card>
            </div>
          </TabsContent>
        </Tabs>
      </div>

      {/* Firehose Sidebar */}
      <div className="w-80 flex-shrink-0 hidden xl:block">
        <Card className="bg-white border border-[#E8EDF5] rounded-xl sticky top-4" data-testid="activity-firehose">
          {/* Firehose Header */}
          <div className="p-4 border-b border-[#E8EDF5]">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Activity className="w-5 h-5 text-[#1B5AFF]" />
                <h3 className="text-[#0A0F1E] font-semibold">Live Activity</h3>
              </div>
              <Button
                onClick={loadFirehose}
                variant="ghost"
                size="sm"
                className="text-[#5B6472] hover:text-[#0A0F1E]"
                disabled={loadingFirehose}
              >
                <RefreshCw className={`w-4 h-4 ${loadingFirehose ? 'animate-spin' : ''}`} />
              </Button>
            </div>
            <p className="text-[#5B6472] text-xs mt-1">The Firehose - Real-time platform events</p>
          </div>

          {/* Firehose Events */}
          <div className="overflow-y-auto max-h-[calc(100vh-200px)]">
            {loadingFirehose && firehoseEvents.length === 0 ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="w-6 h-6 animate-spin text-[#1B5AFF]" />
              </div>
            ) : firehoseEvents.length > 0 ? (
              <div className="divide-y divide-[#E8EDF5]">
                {firehoseEvents.map((event) => (
                  <div 
                    key={event.id} 
                    className="p-3 hover:bg-[#F5F8FF] transition-colors cursor-default"
                  >
                    <div className="flex items-start gap-3">
                      <div className="mt-0.5">
                        {getEventIcon(event.event_type)}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-[#0A0F1E] text-sm leading-tight">
                          {event.message || event.event_type}
                        </p>
                        <p className="text-[#5B6472] text-xs mt-1">
                          {formatRelativeTime(event.timestamp)}
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-[#5B6472]">
                <Bell className="w-8 h-8 mx-auto mb-2 opacity-50" />
                <p className="text-sm">No recent activity</p>
              </div>
            )}
          </div>

          {/* Firehose Footer */}
          <div className="p-3 border-t border-[#E8EDF5] text-center">
            <p className="text-[#5B6472] text-xs">
              Showing last {firehoseEvents.length} events | Auto-refreshes every 30s
            </p>
          </div>
        </Card>
      </div>

      {/* Impersonation Confirmation Dialog */}
      <Dialog open={showImpersonateConfirm} onOpenChange={setShowImpersonateConfirm}>
        <DialogContent className="bg-[#FFFFFF] border-red-500/50">
          <DialogHeader>
            <DialogTitle className="text-[#0A0F1E] flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-red-500" />
              Confirm Impersonation
            </DialogTitle>
            <DialogDescription className="text-[#5B6472]">
              You are about to enter God Mode and view the platform as this user.
            </DialogDescription>
          </DialogHeader>

          {selectedUser && (
            <div className="bg-[#FFFFFF] rounded-lg p-4 my-4">
              <p className="text-[#0A0F1E] font-semibold">{selectedUser.user.email}</p>
              <p className="text-[#5B6472] text-sm">{selectedUser.user.vett_id} | {selectedUser.user.account_type}</p>
            </div>
          )}

          <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-3 text-sm text-red-400">
            <p className="font-semibold mb-1">Warning:</p>
            <ul className="list-disc list-inside space-y-1 text-xs">
              <li>This action is logged and audited</li>
              <li>You will see exactly what this user sees</li>
              <li>Session expires in 2 hours</li>
              <li>A banner will indicate you are impersonating</li>
            </ul>
          </div>

          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              onClick={() => setShowImpersonateConfirm(false)}
              className="border-[#E8EDF5] text-[#0A0F1E]"
            >
              Cancel
            </Button>
            <Button
              onClick={handleImpersonate}
              disabled={impersonating}
              className="bg-red-600 hover:bg-red-700 text-[#FFFFFF]"
              data-testid="confirm-impersonate-btn"
            >
              {impersonating ? (
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
              ) : (
                <Skull className="w-4 h-4 mr-2" />
              )}
              Enter God Mode
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      </div>
    </AdminLayout>
  );
};

export default AdminCommandCenter;
